import { IDENTITY } from '@dadohr/shared/domainTypes/emptyConstants';
import {
  Audience,
  AudienceWithCompletionCriteria,
  ExperienceTemplateMessage,
  getSubTaskTemplates,
  IAutomationTemplate,
  ITaskTemplate,
  RequirementType,
  UserField
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { Rule } from '@dadohr/shared/domainTypes/rules';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormHelperText,
  InputAdornment,
  Switch,
  TextField,
  Tooltip,
  Typography
} from '@material-ui/core';
import { sortBy } from 'lodash';
import {
  CheckSquare,
  ChevronDown,
  Eye,
  HelpCircle,
  Plus,
  PlusCircle,
  Save,
  Slash,
  Trash2,
  Users
} from 'lucide-react';
import * as UX from '../../newux/kit';
import { ClassicBlue600 } from './classicBlue600';
import { useEffect, useRef, useState } from 'react';
import shortid from 'shortid';
import { WithShape } from '../../../../../../components/Charts/Util';
import { ColorPicker } from '../../../../../../components/ColorPicker';
import {
  CustomInputLabel,
  Label
} from '../../../../../../components/CustomInputLabel';
import { DeleteButton } from '../../../../../../components/DeletionConfirmation';
import { DialogTitleWithTypography } from '../../../../../../components/DialogTitleWithTypography';
import { FormRow } from '../../../../../../components/Form/FormRow';
import { useDialogState } from '../../../../../../hooks/useDialogState';
import { useErrorLogger } from '../../../../../../hooks/useErrorLogger';
import {
  useTranslation,
  useUnsafeTranslation
} from '../../../../../../i18next';
import { FlexContainer } from '../../../../../../layout/Flex';
import {
  clearEveryoneNameIfSole,
  filterBlocksForAudience,
  getAudienceName,
  getEveryoneAudience,
  getRandomAudienceColor
} from '../../../../../../services/experiences/audience';
import { useRoutes } from '../../../../../../routes';
import { LightMdToJsx } from '../../../../../../services/formatter';
import {
  useUserRuleConfigWithAutocompletion,
  useUsersByOrgId
} from '../../../../../../services/user';
import { useTheme } from '../../../../../../themes';
import { MatchingUsers } from '../../../../pages/TemplateEdit/StepParticipants';
import {
  EMPTY_RULE_GROUP,
  isEmptyRule,
  RuleBuilder
} from '../../../RuleBuilder';
import { TaskBuilderContext } from '../types';
import {
  CompletionCriteriaSelector,
  getMininumRequiredCount
} from './CompletionCriteria';
import { CopySegmentMenu } from './CopySegmentMenu';
import { createAudienceFromSegment } from './createAudienceFromSegment';
import { SettingsSection, SideContainer } from './layout';
import { SideContainerHeader } from './SideContainerHeader';
import { TaskSettingsOverviewSection } from './TaskSettingsOverviewSection';

const ICON = Users;
export const DEFAULT_AUDIENCE_COLOR = '#BBC1C7';

export const AudienceList = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  marginTop: p.theme.spacing(1),
  columnGap: p.theme.spacing(1.5),
  rowGap: p.theme.spacing(0.5)
}));

export const TaskSettingsOverviewSectionAudiences = <T extends Audience>({
  title,
  audiences,
  onEdit,
  divider
}: {
  title: string;
  audiences: T[];
  onEdit?: () => void;
  divider?: boolean;
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <TaskSettingsOverviewSection
      icon={ICON}
      title={title}
      onEdit={onEdit}
      divider={divider}
    >
      <div>
        {audiences.length === 1 && t('1 Audience')}
        {audiences.length !== 1 &&
          t('{{ count }} Audiences', { count: audiences.length })}
      </div>
      <AudienceList>
        {sortBy(audiences, (a) => a.rule === 'EVERYONE').map((a) => (
          <Typography
            variant="caption"
            component="span"
            key={a.id}
            style={{
              textDecoration: a.blocks.length === 0 ? 'line-through' : 'none'
            }}
          >
            <WithShape
              wrap
              shape="square"
              small
              color={a.color || DEFAULT_AUDIENCE_COLOR}
            >
              {getAudienceName(a, audiences.length > 1, t)}
            </WithShape>
          </Typography>
        ))}
      </AudienceList>
    </TaskSettingsOverviewSection>
  );
};

export const AudienceBlockContainer = styled('div')<{
  selected: boolean;
  noMargin?: boolean;
}>((p) => ({
  border: p.theme.custom.border.standard,
  borderColor: p.selected
    ? p.theme.palette.primary.main
    : p.theme.custom.border.color,
  borderRadius: p.theme.custom.border.radius(0.5),
  padding: `${p.theme.spacing(1.5)}px ${p.theme.spacing(2)}px`,
  marginTop: p.noMargin ? 0 : p.theme.spacing(2),
  cursor: 'pointer',
  width: '100%',

  ':hover': {
    borderColor: p.selected
      ? p.theme.palette.primary.main
      : p.theme.palette.primary.light
  }
}));

export const AudienceBlockTitle = styled('div')((p) => ({
  marginBottom: p.theme.spacing(0.5),

  display: 'flex',
  alignItems: 'center',
  gap: p.theme.spacing()
}));

export const AudienceBlockActions = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginTop: p.theme.spacing(2)
}));

const AudienceBlock = <A extends Audience>({
  value,
  totalBlocks,
  onPreview,
  onEdit,
  hasOtherAudiences,
  selected,
  completionCriteria,
  newUx = false,
  onDelete,
  onChangeColor,
  expanded = false,
  onToggleExpand,
  inlineDetail
}: {
  value: A;
  totalBlocks: number;
  onEdit: () => void;
  onPreview: () => void;
  hasOtherAudiences: boolean;
  selected: boolean;
  completionCriteria?: {
    total: number;
    required: number;
    hasOptional: boolean;
  };
  newUx?: boolean;
  onDelete?: () => void;
  // NEW_UX: recolor a segment inline from the overview swatch (prototype: click
  // the swatch → color grid). Absent for the EVERYONE catch-all.
  onChangeColor?: (color: string) => void;
  // NEW_UX: inline expand — chevron reveals the rules + completion criteria
  // editor (inlineDetail) directly under this row (prototype editor-panels.jsx
  // 540-610), instead of navigating to a separate detail page.
  expanded?: boolean;
  onToggleExpand?: () => void;
  inlineDetail?: React.ReactNode;
}) => {
  const { t } = useUnsafeTranslation();
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const colorPickerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!colorPickerOpen) return;
    const close = (e: MouseEvent) => {
      if (
        colorPickerRef.current &&
        !colorPickerRef.current.contains(e.target as Node)
      ) {
        setColorPickerOpen(false);
      }
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [colorPickerOpen]);

  if (newUx) {
    const color = value.color || DEFAULT_AUDIENCE_COLOR;
    // Empty = no blocks assigned → nothing is sent to this segment (prototype:
    // diagonal slash on the swatch + "Nothing will be sent" tooltip).
    const empty = value.blocks.length === 0;
    const emptyTitle = empty
      ? t('Nothing will be sent to "{{ name }}"', {
          name: getAudienceName(value, hasOtherAudiences, t)
        })
      : undefined;
    // Completion-criteria summary caption (prototype: `completionSummary` under
    // each segment row). Only tasks pass `completionCriteria`; messages and
    // automations have no sub-tasks, so it is absent there — matching classic.
    const completionCaption =
      completionCriteria &&
      (completionCriteria.total > 0 || completionCriteria.hasOptional) ? (
        <div
          style={{
            padding: '0 12px 8px 46px',
            fontSize: 11,
            fontWeight: 300,
            color: '#8C9BAF'
          }}
        >
          {completionCriteria.total === 0
            ? t('Only optional subtasks')
            : t('Complete any {{ n }} of {{ total }} required subtasks', {
                n: completionCriteria.required,
                total: completionCriteria.total
              })}
        </div>
      ) : null;
    // The "Everyone" swatch mirrors its preview tab: green while it's the only
    // audience, black once segments exist (it becomes "Everyone else").
    const isEveryone = value.rule === 'EVERYONE';
    const swatchColor = isEveryone
      ? hasOtherAudiences
        ? UX.tokens.color.black
        : UX.tokens.color.green
      : color;
    const dot = (
      <span
        style={{
          width: 24,
          height: 24,
          borderRadius: 6,
          background: swatchColor,
          backgroundImage:
            empty && (!isEveryone || hasOtherAudiences)
              ? UX.segmentEmptySlash()
              : undefined,
          flex: 'none'
        }}
      />
    );
    // The catch-all "Everyone Else" row (prototype: grey, italic subtitle). Its rule
    // is fixed, but — like any segment — it can be expanded to curate which blocks it
    // gets, edit its completion criteria (for tasks), and (once other segments exist)
    // give it a custom name.
    if (value.rule === 'EVERYONE') {
      return (
        <div
          style={{
            marginBottom: 8,
            borderRadius: 6,
            boxShadow: expanded
              ? 'inset 0 0 0 1px #0061C4'
              : 'inset 0 0 0 1px #DDE0E3',
            overflow: expanded ? 'visible' : 'hidden'
          }}
        >
          {!expanded && (
            <div
              title={emptyTitle}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '10px 12px',
                background: '#FAFAFA',
                cursor: onToggleExpand ? 'pointer' : undefined
              }}
              onClick={onToggleExpand}
            >
              {dot}
              <span
                style={{
                  flex: 1,
                  fontSize: 14,
                  fontWeight: 400,
                  color: '#494F55'
                }}
              >
                {getAudienceName(value, hasOtherAudiences, t)}
              </span>
              {hasOtherAudiences && (
                <span
                  style={{
                    fontSize: 10,
                    fontWeight: 300,
                    color: '#8C9BAF',
                    fontStyle: 'italic'
                  }}
                >
                  {t("Everyone who doesn't match above")}
                </span>
              )}
              {onToggleExpand && (
                <ChevronDown
                  size={16}
                  style={{
                    color: '#9CA3AF',
                    flex: 'none',
                    transform: expanded ? 'rotate(180deg)' : undefined,
                    transition: 'transform .15s'
                  }}
                />
              )}
            </div>
          )}
          {!expanded && completionCaption}
          {expanded && inlineDetail}
        </div>
      );
    }
    return (
      <div
        style={{
          marginBottom: 8,
          borderRadius: 6,
          background: '#fff',
          // Editing (expanded) card and the focused (selected) collapsed row both
          // get the blue600 ring; everything else keeps the grey300 border.
          boxShadow: expanded
            ? 'inset 0 0 0 1px #0061C4'
            : selected
            ? 'inset 0 0 0 1.5px #0061C4'
            : 'inset 0 0 0 1px #DDE0E3',
          overflow: expanded ? 'visible' : 'hidden'
        }}
      >
        {/* When expanded, the inline editor renders its own header (swatch +
            Segment name + trash + chevron), so hide this collapsed header to
            avoid showing the name twice — matches the prototype's single header. */}
        {!expanded && (
        <div
          title={emptyTitle}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 12px',
            cursor: 'pointer'
          }}
          onClick={onToggleExpand || onEdit}
        >
          {onChangeColor ? (
            <div
              ref={colorPickerRef}
              style={{ position: 'relative', flex: 'none' }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                aria-label={t('Change color')}
                onClick={() => setColorPickerOpen((o) => !o)}
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: 6,
                  background: color,
                  backgroundImage: empty ? UX.segmentEmptySlash() : undefined,
                  border: '1px solid #E5E7EB',
                  cursor: 'pointer',
                  padding: 0,
                  display: 'block'
                }}
              />
              {colorPickerOpen && (
                <div
                  style={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    marginTop: 6,
                    zIndex: 20,
                    background: '#fff',
                    borderRadius: 8,
                    boxShadow:
                      '0 4px 16px rgba(0,0,0,.16), 0 0 0 1px rgba(0,0,0,.06)',
                    padding: 10,
                    display: 'grid',
                    gridTemplateColumns: 'repeat(5, 1fr)',
                    gap: 4,
                    width: 160
                  }}
                >
                  {UX.segmentColors.map((c) => (
                    <button
                      key={c.hex}
                      type="button"
                      title={c.name}
                      onClick={() => {
                        onChangeColor(c.hex);
                        setColorPickerOpen(false);
                      }}
                      style={{
                        width: 20,
                        height: 20,
                        borderRadius: 4,
                        background: c.hex,
                        border: '1px solid rgba(0,0,0,.08)',
                        cursor: 'pointer',
                        padding: 0,
                        boxShadow:
                          color === c.hex
                            ? '0 0 0 2px #fff, 0 0 0 3.5px #374151'
                            : 'none'
                      }}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            dot
          )}
          <span style={{ flex: 1, fontSize: 13, fontWeight: 400, color: '#08121A' }}>
            {getAudienceName(value, hasOtherAudiences, t)}
          </span>
          {onDelete && (
            <button
              type="button"
              aria-label={t('Delete')}
              onClick={(e) => {
                e.stopPropagation();
                onDelete();
              }}
              style={{
                border: 0,
                background: 'transparent',
                cursor: 'pointer',
                color: '#9CA3AF',
                display: 'flex',
                padding: 2
              }}
            >
              <Trash2 size={14} />
            </button>
          )}
          {onToggleExpand && (
            <ChevronDown
              size={16}
              style={{
                color: '#9CA3AF',
                flex: 'none',
                transform: expanded ? 'rotate(180deg)' : undefined,
                transition: 'transform .15s'
              }}
            />
          )}
        </div>
        )}
        {!expanded && completionCaption}
        {expanded && inlineDetail}
      </div>
    );
  }

  return (
    <AudienceBlockContainer
      role="button"
      onClick={onPreview}
      selected={selected}
    >
      <AudienceBlockTitle>
        <WithShape
          wrap
          shape="square"
          color={value.color || DEFAULT_AUDIENCE_COLOR}
        >
          <Typography variant="h5">
            {getAudienceName(value, hasOtherAudiences, t)}
          </Typography>
        </WithShape>
      </AudienceBlockTitle>

      <Typography variant="caption">
        <FlexContainer spacing={0.5}>
          {value.blocks.length !== 0 ? (
            <>
              <Eye size={14} />
              <span>
                {t('{{ count }} out of {{ total }} blocks used', {
                  count: value.blocks.length,
                  total: totalBlocks
                })}
              </span>
            </>
          ) : (
            <>
              <Slash size={14} />
              <span>{t("Won't be sent for this audience")}</span>
            </>
          )}
        </FlexContainer>
      </Typography>

      {completionCriteria && (
        <Typography variant="caption">
          <FlexContainer>
            <CheckSquare size={14} />
            <div>
              {t('taskCompletionCriteria.adminFacing.hint', {
                requiredCount: completionCriteria.required,
                count: completionCriteria.total
              })}
            </div>
          </FlexContainer>
        </Typography>
      )}

      <AudienceBlockActions>
        <Button
          size="small"
          color="primary"
          variant="text"
          onClick={(ev) => {
            ev.stopPropagation();
            onEdit();
          }}
        >
          {t('Edit')}
        </Button>

        <Button
          size="small"
          color="primary"
          variant="text"
          onClick={(ev) => {
            ev.stopPropagation();
            onPreview();
          }}
        >
          {selected ? t('Stop previewing') : t('Preview')}
        </Button>
      </AudienceBlockActions>
    </AudienceBlockContainer>
  );
};

/** Round-robin the NEW_UX segment palette by the number of real segments. */
export const nextSegmentColorHex = (audiences: Audience[]): string =>
  UX.segmentColors[
    audiences.filter((a) => a.rule !== 'EVERYONE').length %
      UX.segmentColors.length
  ].hex;

/** Mint a fresh (unsaved) segment seeded with the Everyone blocks. */
export const makeNewAudienceDraft = (
  audiences: Audience[],
  colorHex: string
): Audience => ({
  id: shortid(),
  name: '',
  color: colorHex,
  blocks: [...getEveryoneAudience(audiences).blocks],
  rule: { ...EMPTY_RULE_GROUP }
});

const SettingsAudiencesOverviewCommon = <A extends Audience>({
  title,
  description,
  onBack,
  value,
  goToDetails,
  renderBlock,
  transformNextAudience,
  newUx = false,
  embedded = false,
  expandedAudienceId = null,
  inlineDetail
}: {
  title: string;
  description: string;
  onBack: () => void;

  value: A[];
  goToDetails: (audience: A) => void;
  renderBlock: (audience: A) => React.ReactNode;
  transformNextAudience: (nextAudience: Audience) => A;
  newUx?: boolean;
  // NEW_UX: when embedded inside the Audience panel (below Recipient/CC), drop the
  // SideContainer + panel header and render a "Segments" section label instead.
  embedded?: boolean;
  // NEW_UX inline expand: the currently-expanded/creating audience id, and the
  // detail editor node to render inline. When the id is not in `value` we're
  // creating a new segment, so the editor is rendered as a standalone row.
  expandedAudienceId?: string | null;
  inlineDetail?: React.ReactNode;
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  // NEW_UX segments draw from the prototype's SEG_COLORS token palette,
  // round-robin by segment count (editor-panels.jsx 319); classic keeps the
  // existing random theme colour.
  const nextSegmentColor = () =>
    newUx ? nextSegmentColorHex(value) : getRandomAudienceColor(theme);
  const makeNewAudience = (): Audience =>
    makeNewAudienceDraft(value, nextSegmentColor());
  const content = (
    <>
      {newUx ? (
        embedded ? (
          <>
            <UX.SectionLabel style={{ marginTop: 0 }}>
              {t('Segments & Completion')}
            </UX.SectionLabel>
            <UX.FieldHint style={{ marginTop: 0, marginBottom: 12 }}>
              {t(
                'Curate what certain segments get. Define named segments and assign them to individual blocks.'
              )}
            </UX.FieldHint>
          </>
        ) : (
          <UX.PanelHeader
            icon={<Users size={17} />}
            title={t('Audience')}
            hint={t(
              'Show or hide content to certain participants based on their profile.'
            )}
          />
        )
      ) : (
        <SideContainerHeader
          def={{
            label: title,
            description,
            icon: ICON
          }}
          onBack={onBack}
        />
      )}

      <div
        style={{
          display: 'flex',
          gap: theme.spacing(1),
          flexWrap: 'wrap',
          // Prototype: the Copy/Create button row sits 12px above the segment
          // list (editor-panels.jsx 467).
          marginBottom: newUx ? 12 : undefined
        }}
      >
        {newUx && (
          <CopySegmentMenu
            newUx
            onCopy={(segment) => {
              goToDetails(
                createAudienceFromSegment(
                  segment.data,
                  nextSegmentColor(),
                  transformNextAudience
                )
              );
            }}
          />
        )}
        {newUx ? (
          <UX.RingIconButton
            type="button"
            onClick={() => goToDetails(transformNextAudience(makeNewAudience()))}
          >
            <Plus size={15} strokeWidth={1.5} />
            {t('Create new segment')}
          </UX.RingIconButton>
        ) : (
          <>
            <Button
              size="small"
              color="primary"
              startIcon={<PlusCircle size={20} />}
              onClick={() =>
                goToDetails(transformNextAudience(makeNewAudience()))
              }
            >
              {t('Create New Audience')}
            </Button>
            <CopySegmentMenu
              onCopy={(segment) => {
                goToDetails(
                  createAudienceFromSegment(
                    segment.data,
                    getRandomAudienceColor(theme),
                    transformNextAudience
                  )
                );
              }}
            />
          </>
        )}
      </div>

      {/* Creating a new segment: its draft is not yet in `value`, so render the
          inline editor as a standalone expanded row at the TOP of the list (new
          segments always surface at the top, matching where they land on save). */}
      {newUx &&
        expandedAudienceId &&
        !value.some((a) => a.id === expandedAudienceId) && (
          <div
            style={{
              marginBottom: 8,
              borderRadius: 6,
              background: '#fff',
              boxShadow: 'inset 0 0 0 1px #0061C4',
              overflow: 'visible'
            }}
          >
            {inlineDetail}
          </div>
        )}

      {/* NEW_UX: newest segments sit at the top (they're unshifted on save), and the
          "Everyone" catch-all always sorts to the bottom of the list. */}
      {(newUx
        ? sortBy(value, (a) => (a.rule === 'EVERYONE' ? 1 : 0))
        : value
      ).map(renderBlock)}
    </>
  );
  return embedded ? content : <SideContainer>{content}</SideContainer>;
};

export const TaskSettingsAudiencesOverview = ({
  value,
  onChange,
  title,
  description,
  onBack,
  goToDetails,
  onPreview,
  previewAudienceId,
  newUx = false,
  embedded = false,
  expandedAudienceId = null,
  onToggleExpand,
  inlineDetail
}: {
  title: string;
  description: string;
  onBack: () => void;

  value: ITaskTemplate;
  onChange: React.Dispatch<React.SetStateAction<ITaskTemplate>>;
  expTpl: TaskBuilderContext;
  goToDetails: (audience: AudienceWithCompletionCriteria) => void;
  onPreview: (audienceId: string | null) => void;
  previewAudienceId: string | null;
  newUx?: boolean;
  embedded?: boolean;
  expandedAudienceId?: string | null;
  onToggleExpand?: (audienceId: string | null) => void;
  inlineDetail?: React.ReactNode;
}) => {
  return (
    <SettingsAudiencesOverviewCommon
      title={title}
      description={description}
      onBack={onBack}
      newUx={newUx}
      embedded={embedded}
      value={value.audiences}
      goToDetails={goToDetails}
      expandedAudienceId={expandedAudienceId}
      inlineDetail={inlineDetail}
      transformNextAudience={(a) => ({ ...a, completionCriteria: [] })}
      renderBlock={(a) => {
        const audienceSubTasks = filterBlocksForAudience(
          getSubTaskTemplates(value),
          a
        );
        const totalSubTasksCount = audienceSubTasks.filter(
          (s) => s.requirementType === RequirementType.MANDATORY
        ).length;
        const hasOptionalSubTasks =
          audienceSubTasks.length > totalSubTasksCount;
        const isExpanded = a.id === expandedAudienceId;
        return (
          <AudienceBlock
            key={a.id}
            value={a}
            totalBlocks={value.blocks.length}
            onPreview={() =>
              onPreview(a.id === previewAudienceId ? null : a.id)
            }
            onEdit={() => goToDetails(a)}
            expanded={isExpanded}
            onToggleExpand={
              onToggleExpand
                ? () => onToggleExpand(isExpanded ? null : a.id)
                : undefined
            }
            inlineDetail={isExpanded ? inlineDetail : undefined}
            hasOtherAudiences={value.audiences.length > 1}
            selected={previewAudienceId === a.id}
            newUx={newUx}
            onDelete={() =>
              onChange((v) => ({
                ...v,
                audiences: clearEveryoneNameIfSole(
                  v.audiences.filter((x) => x.id !== a.id)
                )
              }))
            }
            onChangeColor={(color) =>
              onChange((v) => ({
                ...v,
                audiences: v.audiences.map((x) =>
                  x.id === a.id ? { ...x, color } : x
                )
              }))
            }
            completionCriteria={{
              total: totalSubTasksCount,
              required:
                getMininumRequiredCount(a.completionCriteria) ||
                totalSubTasksCount,
              hasOptional: hasOptionalSubTasks
            }}
          />
        );
      }}
    />
  );
};

export const MessageSettingsAudiencesOverview = ({
  value,
  onChange,
  title,
  description,
  onBack,
  goToDetails,
  onPreview,
  previewAudienceId,
  newUx = false,
  embedded = false,
  expandedAudienceId = null,
  onToggleExpand,
  inlineDetail
}: {
  title: string;
  description: string;
  onBack: () => void;

  value: ExperienceTemplateMessage;
  onChange: React.Dispatch<React.SetStateAction<ExperienceTemplateMessage>>;
  expTpl: TaskBuilderContext;
  goToDetails: (audience: Audience) => void;
  onPreview: (audienceId: string | null) => void;
  previewAudienceId: string | null;
  newUx?: boolean;
  embedded?: boolean;
  expandedAudienceId?: string | null;
  onToggleExpand?: (audienceId: string | null) => void;
  inlineDetail?: React.ReactNode;
}) => {
  return (
    <SettingsAudiencesOverviewCommon
      title={title}
      description={description}
      onBack={onBack}
      newUx={newUx}
      embedded={embedded}
      value={value.audiences}
      goToDetails={goToDetails}
      expandedAudienceId={expandedAudienceId}
      inlineDetail={inlineDetail}
      transformNextAudience={IDENTITY}
      renderBlock={(a) => {
        const isExpanded = a.id === expandedAudienceId;
        return (
        <AudienceBlock
          key={a.id}
          value={a}
          totalBlocks={value.blocks.length}
          onPreview={() => onPreview(a.id === previewAudienceId ? null : a.id)}
          onEdit={() => goToDetails(a)}
          expanded={isExpanded}
          onToggleExpand={
            onToggleExpand
              ? () => onToggleExpand(isExpanded ? null : a.id)
              : undefined
          }
          inlineDetail={isExpanded ? inlineDetail : undefined}
          hasOtherAudiences={value.audiences.length > 1}
          selected={previewAudienceId === a.id}
          newUx={newUx}
          onDelete={() =>
            onChange((v) => ({
              ...v,
              audiences: clearEveryoneNameIfSole(
                v.audiences.filter((x) => x.id !== a.id)
              )
            }))
          }
          onChangeColor={(color) =>
            onChange((v) => ({
              ...v,
              audiences: v.audiences.map((x) =>
                x.id === a.id ? { ...x, color } : x
              )
            }))
          }
        />
        );
      }}
    />
  );
};

export const AutomationSettingsAudiencesOverview = ({
  value,
  onChange,
  title,
  description,
  onBack,
  goToDetails,
  onPreview,
  previewAudienceId,
  newUx = false,
  embedded = false,
  expandedAudienceId = null,
  onToggleExpand,
  inlineDetail
}: {
  title: string;
  description: string;
  onBack: () => void;

  value: IAutomationTemplate;
  onChange: React.Dispatch<React.SetStateAction<IAutomationTemplate>>;
  expTpl: TaskBuilderContext;
  goToDetails: (audience: Audience) => void;
  onPreview: (audienceId: string | null) => void;
  previewAudienceId: string | null;
  newUx?: boolean;
  embedded?: boolean;
  expandedAudienceId?: string | null;
  onToggleExpand?: (audienceId: string | null) => void;
  inlineDetail?: React.ReactNode;
}) => {
  return (
    <SettingsAudiencesOverviewCommon
      title={title}
      description={description}
      onBack={onBack}
      newUx={newUx}
      embedded={embedded}
      value={value.audiences}
      goToDetails={goToDetails}
      expandedAudienceId={expandedAudienceId}
      inlineDetail={inlineDetail}
      transformNextAudience={IDENTITY}
      renderBlock={(a) => {
        const isExpanded = a.id === expandedAudienceId;
        return (
        <AudienceBlock
          key={a.id}
          value={a}
          totalBlocks={value.actions.length}
          onPreview={() => onPreview(a.id === previewAudienceId ? null : a.id)}
          onEdit={() => goToDetails(a)}
          expanded={isExpanded}
          onToggleExpand={
            onToggleExpand
              ? () => onToggleExpand(isExpanded ? null : a.id)
              : undefined
          }
          inlineDetail={isExpanded ? inlineDetail : undefined}
          hasOtherAudiences={value.audiences.length > 1}
          selected={previewAudienceId === a.id}
          newUx={newUx}
          onDelete={() =>
            onChange((v) => ({
              ...v,
              audiences: clearEveryoneNameIfSole(
                v.audiences.filter((x) => x.id !== a.id)
              )
            }))
          }
          onChangeColor={(color) =>
            onChange((v) => ({
              ...v,
              audiences: v.audiences.map((x) =>
                x.id === a.id ? { ...x, color } : x
              )
            }))
          }
        />
        );
      }}
    />
  );
};

export const AudienceMatchDialog = ({
  orgId,
  open,
  onClose,
  rule
}: {
  orgId: string;
  open: boolean;
  rule: Rule<UserField>;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [users, loading, error] = useUsersByOrgId(orgId);
  useErrorLogger('users', error);
  return (
    <Dialog maxWidth="md" fullWidth open={open} onClose={onClose}>
      <MatchingUsers users={users} rule={rule} loading={loading} />
      <DialogActions>
        <Button onClick={onClose}>{t('Close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

const RuleBuilderContainer = styled('div')`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

export const SettingsAudiencesDetailsCommon = <A extends Audience>({
  value,
  onChange,
  title,
  description,
  onBack,
  expTpl,

  model,
  onUpdateModel,

  showHiddenBlocks,
  onToggleHiddenBlocks,
  customUserFields,
  children,
  newUx = false,
  inline = false
}: {
  title: string;
  description: string;
  onBack: (nextPreviewedAudienceId?: string) => void;

  value: A[];
  onChange: (nextAudiences: A[]) => void;
  expTpl: TaskBuilderContext;

  model: A;
  onUpdateModel: (nextAudience: A) => void;

  showHiddenBlocks: boolean;
  onToggleHiddenBlocks: () => void;
  customUserFields: CustomUserField[];
  children?: React.ReactNode;
  newUx?: boolean;
  // NEW_UX: render the editor inline (inside an expanded segment row) rather
  // than as a full navigated panel — no SideContainer, no Back button, no
  // "Edit segment" header. Prototype expands the rules + completion in place.
  inline?: boolean;
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const audience = value.find((a) => a.id === model.id);
  const previewDialog = useDialogState();
  const { ROUTES } = useRoutes();

  const config = useUserRuleConfigWithAutocompletion(
    expTpl.orgId,
    customUserFields,
    expTpl.tz,
    {
      orgId: expTpl.orgId,
      experienceTemplateId: expTpl.templateId
    }
  );
  const audienceName = getAudienceName(model, value.length > 1, t, {
    noDefault: true
  });
  // Composed/suffixed form (vs. audienceName's raw editable value) — for display
  // text like the "is the default audience" caption below, not the input itself.
  const audienceDisplayName = getAudienceName(model, value.length > 1, t);

  // Promotes this task-scoped draft into the org's reusable Saved Segment
  // Library — opens the "new audience" builder in a new tab, with the
  // current name + rule prefilled via a URL query param (not router state:
  // a new tab is a separate JS runtime, so state passed to history.push
  // can't cross that boundary — the payload has to travel in the URL
  // itself).
  const addToSegmentLibrary = () => {
    if (model.rule === 'EVERYONE') return;
    const url = new URL(
      ROUTES.users.targetAudiences.new.url(),
      window.location.origin
    );
    url.searchParams.set(
      'prefill',
      JSON.stringify({ name: model.name, rule: model.rule })
    );
    window.open(url.toString(), '_blank');
  };

  const [errors, setErrors] = useState<Set<'NO_NAME' | 'NO_RULE'>>(new Set());

  const leaveDialog = useDialogState();

  // - The default audience cannot be removed
  // - When audience is not defined, we're creating a new one - which hasn't been saved yet,
  //   so it also can't be removed
  const isRemovable = model.rule !== 'EVERYONE' && !!audience;
  // Editing an existing segment inline (vs. creating a new one) — the name field
  // becomes the card header and the "Segment name" label is dropped.
  const isEditingExistingInline = inline && !!audience;
  const onBackFromDetail = () => {
    if (audience === model) {
      onBack(audience?.id);
    }
    leaveDialog.openDialog();
  };
  const removeSegment = () => {
    onChange(clearEveryoneNameIfSole(value.filter((a) => a.id !== model.id)));
    onBack();
  };
  const body = (
    <>
      {inline ? null : (
        <SideContainerHeader
          def={{
            label: title,
            description,
            icon: ICON
          }}
          onBack={onBackFromDetail}
          newUx={newUx}
        />
      )}

      <SettingsSection style={newUx ? { padding: '0 20px' } : undefined}>
        {newUx ? (
          <>
            {/* Editing an existing segment inline: the name input IS the card header
                (prototype "EDITING EXISTING ONE"), so drop the "Segment name" title.
                Creating a new one keeps the labelled field. */}
            {!isEditingExistingInline && (
              <UX.FieldLabel style={inline ? { marginTop: 12 } : undefined}>
                {t('Segment name')}
              </UX.FieldLabel>
            )}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <ClassicBlue600 style={{ flex: 1 }}>
                <TextField
                  variant="outlined"
                  fullWidth
                  value={audienceName}
                  placeholder={
                    model.rule === 'EVERYONE' ? t('Everyone else') : t('Enter a name')
                  }
                  disabled={model.rule === 'EVERYONE' && value.length <= 1}
                  error={errors.has('NO_NAME')}
                  onChange={(ev) =>
                    onUpdateModel({ ...model, name: ev.target.value })
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ColorPicker
                          size={20}
                          value={model.color || DEFAULT_AUDIENCE_COLOR}
                          onChange={(nextColor) =>
                            onUpdateModel({ ...model, color: nextColor })
                          }
                          disabled={model.rule === 'EVERYONE'}
                        />
                      </InputAdornment>
                    )
                  }}
                />
              </ClassicBlue600>
              {/* Inline (expanded-in-list) editor: delete + collapse sit beside the
                  header input (prototype layout). */}
              {inline && (
                <>
                  {isRemovable && (
                    <button
                      type="button"
                      aria-label={t('Delete')}
                      onClick={removeSegment}
                      style={{
                        border: 0,
                        background: 'transparent',
                        cursor: 'pointer',
                        color: '#9CA3AF',
                        display: 'flex',
                        padding: 2,
                        flex: 'none'
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                  {/* Saved segment → collapse chevron; a brand-new (unsaved) draft has
                      nothing to collapse back to, so it gets a discard trash instead. */}
                  {audience ? (
                    <button
                      type="button"
                      aria-label={t('Collapse')}
                      onClick={onBackFromDetail}
                      style={{
                        border: 0,
                        background: 'transparent',
                        cursor: 'pointer',
                        color: '#9CA3AF',
                        display: 'flex',
                        padding: 2,
                        flex: 'none'
                      }}
                    >
                      <ChevronDown size={16} style={{ transform: 'rotate(180deg)' }} />
                    </button>
                  ) : (
                    <button
                      type="button"
                      aria-label={t('Discard')}
                      onClick={removeSegment}
                      style={{
                        border: 0,
                        background: 'transparent',
                        cursor: 'pointer',
                        color: '#9CA3AF',
                        display: 'flex',
                        padding: 2,
                        flex: 'none'
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  )}
                </>
              )}
            </div>
            {errors.has('NO_NAME') && (
              <div style={{ color: '#E25E50', fontSize: 12, marginTop: 4 }}>
                {t('Please provide a name')}
              </div>
            )}
            {isEditingExistingInline && (
              <div
                style={{
                  height: 1,
                  background: '#E5E7EB',
                  margin: '12px -20px 0'
                }}
              />
            )}
          </>
        ) : (
          <FormRow alignItems="center">
            <TextField
              label={t('Audience Name')}
              placeholder={
                model.rule === 'EVERYONE' ? t('Everyone else') : t('Enter a name')
              }
              value={audienceName}
              onChange={(ev) =>
                onUpdateModel({ ...model, name: ev.target.value })
              }
              error={errors.has('NO_NAME')}
              helperText={errors.has('NO_NAME') && t('Please provide a name')}
              disabled={model.rule === 'EVERYONE' && value.length <= 1}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <ColorPicker
                      size={24}
                      value={model.color || DEFAULT_AUDIENCE_COLOR}
                      onChange={(nextColor) =>
                        onUpdateModel({ ...model, color: nextColor })
                      }
                      disabled={model.rule === 'EVERYONE'}
                    />
                  </InputAdornment>
                )
              }}
            />
          </FormRow>
        )}
      </SettingsSection>

      <SettingsSection style={newUx ? { padding: '0 20px' } : undefined}>
        {model.rule === 'EVERYONE' && (
          <LightMdToJsx>
            {t(
              '**{{ name }}** is the default audience. You can select blocks, but you cannot set rules.',
              { name: audienceDisplayName }
            )}
          </LightMdToJsx>
        )}
        {model.rule !== 'EVERYONE' && (
          <>
            <CustomInputLabel fullWidth label={t('Participant Filters')}>
              <RuleBuilderContainer>
                <RuleBuilder
                  value={model.rule}
                  onChange={(nextRule) =>
                    onUpdateModel({ ...model, rule: nextRule })
                  }
                  config={config}
                  variant="compact"
                  nullable
                  showEmptyRowWhenNull={false}
                  newUx={newUx}
                  extraRootActions={
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      startIcon={<Save size={18} />}
                      style={
                        newUx
                          ? { color: '#0061C4', borderColor: '#0061C4' }
                          : undefined
                      }
                      onClick={addToSegmentLibrary}
                    >
                      <span style={{ fontSize: 10 }}>
                        {t('Add to segment library')}
                      </span>
                    </Button>
                  }
                />
                {errors.has('NO_RULE') && (
                  <FormHelperText error>
                    {t('Please provide at least one rule')}
                  </FormHelperText>
                )}
                <Button
                  onClick={previewDialog.openDialog}
                  startIcon={<Eye size={16} />}
                  color="primary"
                  style={{
                    alignSelf: 'flex-end',
                    ...(newUx ? { color: '#0061C4' } : {})
                  }}
                >
                  {t('Preview matching employees')}
                </Button>
                <AudienceMatchDialog
                  orgId={expTpl.orgId}
                  open={previewDialog.dialogOpen}
                  onClose={previewDialog.closeDialog}
                  rule={model.rule}
                />
              </RuleBuilderContainer>
            </CustomInputLabel>
          </>
        )}
      </SettingsSection>

      {/* Bulk-clear shortcut for "no blocks reference this segment" — the same
          end state as unassigning it from every block's share menu one at a
          time. Derived from model.blocks, not a separate flag: on() reflects
          the existing empty-segment check (see AudienceBlock's `empty`
          above), and clicking only ever clears — there's no way to undo from
          here, matching how the manual per-block removal it replaces has no
          bulk "undo" either. */}
      <SettingsSection style={newUx ? { padding: '0 20px' } : undefined}>
        {newUx ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <UX.Toggle
              on={model.blocks.length === 0}
              ariaLabel={t("Don't send to this segment")}
              onChange={() => {
                if (model.blocks.length > 0) {
                  onUpdateModel({ ...model, blocks: [] });
                }
              }}
            />
            <span style={{ fontSize: 13 }}>
              {t("Don't send to this segment")}
            </span>
            {model.blocks.length === 0 && (
              <Tooltip
                title={t(
                  'Add content blocks to the segment to re-enable sending.'
                )}
              >
                <HelpCircle size={16} color={theme.custom.colors.gray.w500} />
              </Tooltip>
            )}
          </div>
        ) : (
          <FormControlLabel
            control={
              <Switch
                checked={model.blocks.length === 0}
                onChange={() => {
                  if (model.blocks.length > 0) {
                    onUpdateModel({ ...model, blocks: [] });
                  }
                }}
                color="primary"
              />
            }
            label={
              <FlexContainer alignItems="center">
                {t("Don't send to this segment")}
                {model.blocks.length === 0 && (
                  <Tooltip
                    title={t(
                      'Add content blocks to the segment to re-enable sending.'
                    )}
                  >
                    <HelpCircle
                      size={16}
                      color={theme.custom.colors.gray.w500}
                    />
                  </Tooltip>
                )}
              </FlexContainer>
            }
          />
        )}
      </SettingsSection>

      {children}

      {/* Inline (expanded-in-list) segment editor omits the "show hidden blocks"
          toggle to match the prototype; the navigated/classic layouts keep it. */}
      {!inline && (
        <SettingsSection style={newUx ? { padding: '0 20px' } : undefined}>
          {newUx ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <UX.Toggle
                on={showHiddenBlocks}
                ariaLabel={t('Show blocks hidden for this audience')}
                onChange={() => onToggleHiddenBlocks()}
              />
              <span style={{ fontSize: 13 }}>
                {t('Show blocks hidden for this audience')}
              </span>
            </div>
          ) : (
            <FormControlLabel
              control={
                <Switch
                  checked={showHiddenBlocks}
                  onChange={() => onToggleHiddenBlocks()}
                  color="primary"
                />
              }
              label={t('Show blocks hidden for this audience')}
            />
          )}
        </SettingsSection>
      )}

      <FlexContainer
        justifyContent="space-between"
        style={newUx ? { padding: '0 20px 20px' } : undefined}
      >
        {/* Inline editor puts delete in the header row (trash icon), so only the
            navigated/classic layout shows the bottom "Remove audience" button. */}
        {(!isRemovable || inline) && <div style={{ minWidth: 5 }} />}
        {isRemovable && !inline && (
          <DeleteButton
            onDelete={async () => {
              onChange(clearEveryoneNameIfSole(value.filter((a) => a.id !== model.id)));
              onBack();
            }}
            color="secondary"
            startIcon={<Trash2 size={16} />}
            variant="outlined"
          >
            {t('Remove audience')}
          </DeleteButton>
        )}
        <Button
          variant="outlined"
          color="primary"
          disabled={audience === model}
          onClick={() => {
            const nextErrors = new Set<'NO_RULE' | 'NO_NAME'>();
            if (model.rule !== 'EVERYONE') {
              if (!model.name) {
                nextErrors.add('NO_NAME');
              }
              if (isEmptyRule(model.rule)) {
                nextErrors.add('NO_RULE');
              }
            }
            setErrors(nextErrors);
            if (nextErrors.size) {
              return;
            }

            const i = value.findIndex((a) => a.id === model.id);
            const nextAudiences = [...value];
            if (i === -1) {
              // for new audiences
              nextAudiences.unshift(model);
            } else {
              nextAudiences[i] = model;
            }
            onChange(nextAudiences);
            onBack(model.id);
          }}
        >
          {audience ? t('Save changes') : t('Save')}
        </Button>
      </FlexContainer>

      <Dialog
        open={leaveDialog.dialogOpen}
        onClose={leaveDialog.closeDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitleWithTypography>
          {t('Are you sure?')}
        </DialogTitleWithTypography>
        <DialogContent>
          {t(
            'This audience has unsaved changes. Please confirm to discard them.'
          )}
        </DialogContent>
        <DialogActions>
          <Button color="primary" onClick={leaveDialog.closeDialog}>
            {t('Cancel')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => onBack(audience?.id)}
          >
            {t('Discard changes')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  return inline ? (
    // Card border/radius/background come from the surrounding segment row
    // (grey300, 6px, white) — this wrapper stays transparent (prototype img 6).
    <div style={{ padding: '4px 0 14px' }}>{body}</div>
  ) : (
    <SideContainer>{body}</SideContainer>
  );
};

export const TaskSettingsAudiencesDetails = ({
  value,
  onChange,
  title,
  description,
  onBack,
  expTpl,

  model,
  onUpdateModel,

  showHiddenBlocks,
  onToggleHiddenBlocks,
  customUserFields,
  newUx = false,
  inline = false
}: {
  title: string;
  description: string;
  onBack: (nextPreviewedAudienceId?: string) => void;

  value: ITaskTemplate;
  onChange: React.Dispatch<React.SetStateAction<ITaskTemplate>>;
  expTpl: TaskBuilderContext;

  model: AudienceWithCompletionCriteria;
  onUpdateModel: (nextAudience: AudienceWithCompletionCriteria) => void;

  showHiddenBlocks: boolean;
  onToggleHiddenBlocks: () => void;
  customUserFields: CustomUserField[];
  newUx?: boolean;
  inline?: boolean;
}) => {
  const { t } = useUnsafeTranslation();

  return (
    <SettingsAudiencesDetailsCommon
      inline={inline}
      title={title}
      description={description}
      onBack={onBack}
      expTpl={expTpl}
      value={value.audiences}
      onChange={(nextAudiencs) =>
        onChange((x) => ({ ...x, audiences: nextAudiencs }))
      }
      model={model}
      onUpdateModel={onUpdateModel}
      showHiddenBlocks={showHiddenBlocks}
      onToggleHiddenBlocks={onToggleHiddenBlocks}
      customUserFields={customUserFields}
      newUx={newUx}
    >
      {newUx ? (
        <SettingsSection style={{ padding: '0 20px' }}>
          <div
            style={{
              background: UX.tokens.color.white,
              borderRadius: UX.tokens.radius.md
            }}
          >
            <UX.FieldLabel>{t('Completion criteria')}</UX.FieldLabel>
            <CompletionCriteriaSelector
              value={model.completionCriteria}
              onChange={(criteria) =>
                onUpdateModel({ ...model, completionCriteria: criteria })
              }
              subTasks={filterBlocksForAudience(getSubTaskTemplates(value), model)}
              newUx={newUx}
            />
          </div>
        </SettingsSection>
      ) : (
        <SettingsSection>
          <Label label={t('Completion criteria')} />
          <CompletionCriteriaSelector
            value={model.completionCriteria}
            onChange={(criteria) =>
              onUpdateModel({ ...model, completionCriteria: criteria })
            }
            subTasks={filterBlocksForAudience(getSubTaskTemplates(value), model)}
            newUx={newUx}
          />
          <LightMdToJsx>
            {t(
              'Optional sub-tasks are not considered when determining whether the task is complete.'
            )}
          </LightMdToJsx>
        </SettingsSection>
      )}
    </SettingsAudiencesDetailsCommon>
  );
};

export const MessageSettingsAudiencesDetails = ({
  value,
  onChange,
  title,
  description,
  onBack,
  expTpl,

  model,
  onUpdateModel,

  showHiddenBlocks,
  onToggleHiddenBlocks,
  customUserFields,
  newUx = false,
  inline = false
}: {
  title: string;
  description: string;
  onBack: (nextPreviewedAudienceId?: string) => void;

  value: ExperienceTemplateMessage;
  onChange: React.Dispatch<React.SetStateAction<ExperienceTemplateMessage>>;
  expTpl: TaskBuilderContext;

  model: Audience;
  onUpdateModel: (nextAudience: Audience) => void;

  showHiddenBlocks: boolean;
  onToggleHiddenBlocks: () => void;
  customUserFields: CustomUserField[];
  newUx?: boolean;
  inline?: boolean;
}) => {
  return (
    <SettingsAudiencesDetailsCommon
      inline={inline}
      title={title}
      description={description}
      onBack={onBack}
      expTpl={expTpl}
      value={value.audiences}
      onChange={(nextAudiencs) =>
        onChange((x) => ({ ...x, audiences: nextAudiencs }))
      }
      model={model}
      onUpdateModel={onUpdateModel}
      showHiddenBlocks={showHiddenBlocks}
      onToggleHiddenBlocks={onToggleHiddenBlocks}
      customUserFields={customUserFields}
      newUx={newUx}
    />
  );
};

export const AutomationSettingsAudiencesDetails = ({
  value,
  onChange,
  title,
  description,
  onBack,
  expTpl,

  model,
  onUpdateModel,

  showHiddenBlocks,
  onToggleHiddenBlocks,
  customUserFields,
  newUx = false,
  inline = false
}: {
  title: string;
  description: string;
  onBack: (nextPreviewedAudienceId?: string) => void;

  value: IAutomationTemplate;
  onChange: React.Dispatch<React.SetStateAction<IAutomationTemplate>>;
  expTpl: TaskBuilderContext;

  model: Audience;
  onUpdateModel: (nextAudience: Audience) => void;

  showHiddenBlocks: boolean;
  onToggleHiddenBlocks: () => void;
  customUserFields: CustomUserField[];
  newUx?: boolean;
  inline?: boolean;
}) => {
  return (
    <SettingsAudiencesDetailsCommon
      title={title}
      description={description}
      onBack={onBack}
      expTpl={expTpl}
      value={value.audiences}
      onChange={(nextAudiencs) =>
        onChange((x) => ({ ...x, audiences: nextAudiencs }))
      }
      model={model}
      onUpdateModel={onUpdateModel}
      showHiddenBlocks={showHiddenBlocks}
      onToggleHiddenBlocks={onToggleHiddenBlocks}
      customUserFields={customUserFields}
      newUx={newUx}
      inline={inline}
    />
  );
};
